import React, { useState, useCallback } from 'react';
import { PickingPageViewProps } from './types';
import { initialPagination } from 'services/accounting';
import { ObjectType } from 'services/customFields';
import { CustomFieldsModal } from 'ui/components/CustomFields/CustomFieldsModal';
import { ConfirmationModal } from 'ui/components/Modal/ConfirmationModal';
import { PageWithAdvancedSearch } from 'ui/components/Page/PageWithAdvancedSearch';
import { PaperSlidingLayout } from 'ui/components/Paper/PaperSlidingLayout';
import {
  PICKING_COLUMNS,
  PickingAdvancedSearch,
  PickingSearchResults,
  PickingDetailsCard,
} from './components';
import {
  initialPickingFormValues,
  advancedSearchReduxActions,
  displayNameMap,
  createDisplayValueMap,
} from './consts';
import { usePickingActionModals } from './hooks/usePickingActionModals';

export default function PickingPageView(props: PickingPageViewProps) {
  const {
    data: {
      searchResult,
      activePickId,
      orderPriorities,
      activeDate,
      searchTags,
      searchItem,
      customers,
      locations,
      users,
    },
    uiState: { isLoadingSearchResult, isVoiding, isGrouping },
    selection: { selectedItems, setSelectedItems },
    handlers: {
      fetchSearchResult,
      handlePickClicked,
      handleDetailsCardClosed,
      handlePaginationChange,
      handlePageAction,
      onPrintPickTicketsClicked,
      executePickingBulkAction,
    },
  } = props;

  const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false);

  const handleShowCustomFieldsModalRequest = useCallback(() => {
    setIsCustomFieldsModalOpen(true);
  }, []);

  const {
    isVoidModalOpen,
    openVoidModal,
    closeVoidModal,
    handleConfirmVoid,
    isGroupModalOpen,
    openGroupModal,
    closeGroupModal,
    handleConfirmGroup,
  } = usePickingActionModals({
    executePickingBulkAction,
    isVoiding,
    isGrouping,
  });

  return (
    <>
      <PageWithAdvancedSearch
        detailCardColumns={PICKING_COLUMNS(orderPriorities, activeDate)}
        initialFormValues={initialPickingFormValues}
        advancedSearchReduxActions={advancedSearchReduxActions}
        searchResult={searchResult}
        fetchSearchResult={fetchSearchResult}
        AdvancedSearchFieldsCmp={PickingAdvancedSearch}
        displayNameMap={displayNameMap}
        displayValueMap={createDisplayValueMap(
          orderPriorities,
          searchItem,
          customers,
          locations,
          users,
          searchTags
        )}
        showAllLabel="Show All Picks"
        pageName="Picking"
      >
        <PaperSlidingLayout shown={Boolean(activePickId)}>
          <PickingSearchResults
            picks={searchResult.items}
            activePickId={activePickId}
            handlePickClick={handlePickClicked}
            isLoadingPicks={isLoadingSearchResult}
            pagination={searchResult.pagination || initialPagination}
            activeDate={activeDate}
            onPaginationChange={handlePaginationChange}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            onPageAction={handlePageAction}
            onRequestShowVoidModal={openVoidModal}
            onRequestShowGroupModal={openGroupModal}
            onPrintPickTicketsClicked={onPrintPickTicketsClicked}
            onShowCustomFieldsModalRequest={handleShowCustomFieldsModalRequest}
            isVoiding={isVoiding}
            isGrouping={isGrouping}
          />
          <PickingDetailsCard
            activePickId={activePickId}
            onClose={handleDetailsCardClosed}
            fetchSearchResults={fetchSearchResult}
          />
        </PaperSlidingLayout>
      </PageWithAdvancedSearch>
      <CustomFieldsModal
        open={isCustomFieldsModalOpen}
        setVisible={setIsCustomFieldsModalOpen}
        module={ObjectType.Pick}
      />
      <ConfirmationModal
        open={isVoidModalOpen}
        title="Void Picking"
        body={`This will void all selected picks, are you sure?`}
        onCancelClicked={closeVoidModal}
        onConfirmClicked={handleConfirmVoid}
        confirmLabel="Void"
        confirmButtonRed
        isLoading={isVoiding}
      />
      <ConfirmationModal
        open={isGroupModalOpen}
        title="Group Picking"
        body={`This will group all selected picks, are you sure?`}
        onCancelClicked={closeGroupModal}
        onConfirmClicked={handleConfirmGroup}
        confirmLabel="Group"
        confirmButtonRed
        isLoading={isGrouping}
      />
    </>
  );
}
